import styled from '@emotion/styled';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { isValidRRCustomerId } from '@racemap/utilities/functions/validation';
import type { RRCustomerIdInfo } from '@racemap/utilities/types/raceResult';
import { Button, Input, Space, Table, type TableProps } from 'antd';
import { type CSSProperties, type FC, type PropsWithChildren, useState } from 'react';
import { DocuLink } from 'src/components/BasicComponents';
import { Placeholder } from 'src/components/BasicComponents/Placeholder';
import useSWR from 'swr';
import { useStore } from '../../../store/reducers';
import {
  IconExclamationTriangle,
  IconFail,
  IconOk,
  IconPlus,
  IconTrash,
} from '../../BasicComponents/Icons';
import { VStack } from '../../BasicComponents/MetaComponent';
import { PopoverHint } from '../../BasicComponents/PopoverHint';

const apiClient = RacemapAPIClient.fromWindowLocation();

export const CustomerIdsConfiguration: FC = () => {
  return (
    <VStack>
      <p>
        Racemap tries to find reads for all listed RR customer Id's -{' '}
        <DocuLink
          as="href"
          path="predictive-tracking-with-data-from-timekeeping/race-or-result"
          style={{ marginLeft: 3 }}
        />
        <PopoverHint>
          Add further customer ids to consider reads from additional hardware in the race
          visualization.
        </PopoverHint>
      </p>
      <AddCustomerId />

      <CustomerIdsTable />
    </VStack>
  );
};

function CustomerIdsTable() {
  const event = useStore((s) => s.events.getter.currentEvent());
  const { updateEvent } = useStore((s) => s.events.actions);
  const { setReadsFilter } = useStore((s) => s.prediction.actions);

  if (event == null) return <></>;

  const customerIds = event.integrations.raceresult.customerIds || [];

  function deleteCustomerId(customerId: string) {
    if (event == null) return;

    const newAdditionalCustomIds = customerIds.filter((id: string) => id !== customerId);
    updateEvent(event.id, [
      { key: 'integrations.raceresult.customerIds', newValue: newAdditionalCustomIds },
    ]);
    setReadsFilter({ userIds: [...newAdditionalCustomIds, ...event.editorIds] });
  }

  const dataSource = customerIds.map((id) => ({
    key: id,
    customerId: id,
  }));

  const columns: TableProps['columns'] = [
    {
      title: 'Customer ID',
      dataIndex: 'customerId',
      key: 'customerId',
    },
    {
      title: 'Validation',
      dataIndex: 'customerId',
      key: 'validation',
      align: 'center',
      render: (customerId: string) => (
        <TableCell
          title={isValidRRCustomerId(customerId) ? 'Valid Customer ID' : 'Invalid Customer ID'}
          status={isValidRRCustomerId(customerId) ? 'success' : 'danger'}
        />
      ),
    },
    {
      title: 'Tracking Partner',
      dataIndex: 'customerId',
      key: 'trackingPartner',
      align: 'center',
      render: (customerId: string) => <TrackingPartnerCell customerId={customerId} />,
    },
    {
      title: 'Actions',
      dataIndex: 'customerId',
      key: 'actions',
      align: 'center',
      render: (customerId: string) => (
        <IconTrash style={{ cursor: 'pointer' }} onClick={() => deleteCustomerId(customerId)} />
      ),
    },
  ];

  return <Table dataSource={dataSource} columns={columns} pagination={false} bordered />;
}

function TableCell({
  status,
  children,
  title,
}: PropsWithChildren<{ status: 'success' | 'warning' | 'danger'; title?: string }>) {
  switch (status) {
    case 'danger':
      return (
        <CellContent title={title}>
          <IconFail style={{ cursor: 'help', color: RacemapColors.DarkRed }} />
          {children}
        </CellContent>
      );
    case 'warning':
      return (
        <CellContent title={title}>
          <IconExclamationTriangle style={{ cursor: 'help', color: RacemapColors.DarkYellow }} />
          {children}
        </CellContent>
      );
    case 'success':
      return (
        <CellContent title={title}>
          <IconOk style={{ cursor: 'help', color: RacemapColors.BaseGreen }} />
          {children}
        </CellContent>
      );
  }
}

const TrackingPartnerCell = ({
  customerId,
}: {
  customerId: string;
}) => {
  const { data: rrCustomerIdInfo, isLoading } = useSWR<RRCustomerIdInfo>(
    ['rrCustomerIdInfo', customerId],
    async ([, customerId]: [string, string]) => {
      return await apiClient.getInfosForRRCustomerId(customerId);
    },
    { refreshInterval: OneMinuteInMillis, keepPreviousData: false },
  );

  if (isLoading) return <Placeholder width={70} />;

  return (
    <TableCell
      title={`The current tracking partner is "${rrCustomerIdInfo?.forwardingPartner}"`}
      status={rrCustomerIdInfo?.isFPCorrect ? 'success' : 'danger'}
    />
  );
};

const addCustomerIdStyles: CSSProperties = {
  marginBottom: '20px',
  width: '100%',
};

const messageStyle: CSSProperties = {
  color: RacemapColors.DarkRed,
  marginTop: 5,
};

function AddCustomerId() {
  const event = useStore((s) => s.events.getter.currentEvent());
  const { updateEvent } = useStore((s) => s.events.actions);
  const { setReadsFilter } = useStore((s) => s.prediction.actions);

  const customerIds = new Set(event?.integrations.raceresult.customerIds || []);
  const editorIds = event?.editorIds || [];

  const [inputId, setInputId] = useState('');
  const [triedToSave, setTriedToSave] = useState(false);

  if (event == null) return <></>;

  const isDuplicateId = customerIds.has(inputId);
  const isInvalidId = inputId == null || Number.isNaN(Number.parseInt(inputId, 10));

  function handleButtonClick() {
    if (event == null) return;
    if (inputId === '') return setTriedToSave(false);
    if (isDuplicateId || isInvalidId) return setTriedToSave(true);

    setTriedToSave(false);
    updateEvent(event.id, [
      {
        key: 'integrations.raceresult.customerIds',
        newValue: Array.from(customerIds).concat(inputId),
      },
    ]).then(() => {
      setReadsFilter({ userIds: Array.from(customerIds).concat(inputId).concat(editorIds) });
      setInputId('');
    });
  }

  return (
    <div style={addCustomerIdStyles}>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          placeholder="Add additional Customer ID"
          aria-label="Add additional Customer ID"
          value={inputId}
          onChange={({ target }) => setInputId(target.value)}
        />
        <Button type="primary" icon={<IconPlus />} onClick={handleButtonClick} />
      </Space.Compact>

      {triedToSave && isDuplicateId && (
        <div style={messageStyle}>The Customer ID is already connected with the Event.</div>
      )}
      {triedToSave && isInvalidId && (
        <div style={messageStyle}>Invalid ID. Only digits are valid.</div>
      )}
    </div>
  );
}

const CellContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;

  svg {
    margin-right: 5px;
  }
`;
